import { Injectable, inject } from '@angular/core';
import { CreateFormParams, Form, UpdateFormParams } from '../models/form.model';
import { DatabaseService, type QuerySignal } from './database.service';
import { LoggerService } from './logger.service';

/**
 * Service for managing forms
 * Handles CRUD operations for interaction type entities
 */
@Injectable({
  providedIn: 'root',
})
export class FormService {
  private db = inject(DatabaseService);

  private logger = inject(LoggerService).getLogger('FormService');

  /** Maps an `interaction_types` row (data JSON + timestamps) to a {@link Form}. */
  private rowToForm(row: any): Form {
    const data = JSON.parse((row.data as string) || '{}');
    return {
      id: row.id as string,
      name: data.name || '',
      form_definition_id: data.form_definition_id || undefined,
      prefill_last_values: Boolean(data.prefill_last_values),
      auto_submit: Boolean(data.auto_submit),
      play_success_sound: Boolean(data.play_success_sound),
      sample_scenario: data.sample_scenario || undefined,
      created_at: row.created_at as string | undefined,
      updated_at: row.updated_at as string | undefined,
    };
  }

  /**
   * Get all forms
   */
  async getAll(): Promise<Form[]> {
    await this.db.init();

    try {
      const results = await this.db.exec(`
        SELECT id, data, created_at, updated_at
        FROM interaction_types
        ORDER BY json_extract(data, '$.name')
      `);

      this.logger.debug('Loaded forms:', results);

      return results.map((row) => this.rowToForm(row));
    } catch (error) {
      this.logger.error('Failed to get forms:', error);
      throw error;
    }
  }

  /**
   * Count custom forms toward the plan limit. Onboarding sample forms are
   * excluded so trying a sample scenario never blocks creating real forms.
   */
  async count(): Promise<number> {
    await this.db.init();

    const results = await this.db.exec(
      `SELECT COUNT(*) as count FROM interaction_types
       WHERE json_extract(data, '$.sample_scenario') IS NULL`,
    );
    return results[0]?.count || 0;
  }

  /**
   * Create a reactive query for forms
   * Returns signals that automatically update when data changes via sync
   *
   * @returns QuerySignal with data, loading, and error signals
   *
   * @example
   * // In component
   * query = this.interactionTypeService.createQuery();
   * interactionTypes = this.query.data;
   * loading = this.query.loading;
   *
   * // Template automatically re-renders when data changes
   * @if (loading()) { <spinner/> }
   * @else {
   *   @for (type of interactionTypes(); track type.id) {
   *     <div>{{type.name}}</div>
   *   }
   * }
   */
  createQuery(): QuerySignal<Form[]> {
    return this.db.createQuery('interaction_types', () => this.getAll());
  }

  /**
   * Create a reactive query for forms assigned to an asset
   * Returns signals that automatically update when data changes via sync
   */
  createQueryByCheckPointId(checkpointId: string): QuerySignal<Form[]> {
    return this.db.createQuery('asset_interaction_types', () =>
      this.getByCheckPointId(checkpointId),
    );
  }

  /**
   * Get an interaction type by ID
   */
  async getById(id: string): Promise<Form | null> {
    await this.db.init();

    try {
      const results = await this.db.exec(
        `SELECT id, data, created_by, created_at, updated_by, updated_at
         FROM interaction_types
         WHERE id = ?`,
        [id],
      );

      if (results.length === 0) {
        this.logger.warn(`Interaction type not found: ${id}`);
        return null;
      }

      const form = this.rowToForm(results[0]);

      this.logger.debug('Loaded interaction type:', form);

      return form;
    } catch (error) {
      this.logger.error('Failed to get interaction type:', {
        id,
        error,
      });
      throw error;
    }
  }

  /**
   * Create a new interaction type
   */
  async create(params: CreateFormParams): Promise<Form> {
    await this.db.init();

    try {
      const id = this.db.generateId();
      const now = Date.now();

      const data: Record<string, unknown> = {
        name: params.name,
        form_definition_id: params.form_definition_id,
        prefill_last_values: params.prefill_last_values || false,
        auto_submit: params.auto_submit || false,
        play_success_sound: params.play_success_sound || false,
      };
      if (params.sample_scenario) {
        data['sample_scenario'] = params.sample_scenario;
      }

      await this.db.run(
        `INSERT INTO interaction_types (id, data, created_by, created_at, updated_by, updated_at)
         VALUES (?, ?, ?, ?, ?, ?)`,
        [
          id,
          JSON.stringify(data),
          '', // TODO: Add created_by when user context is available
          now,
          '', // TODO: Add updated_by when user context is available
          now,
        ],
      );

      const created: Form = {
        id,
        name: params.name,
        form_definition_id: params.form_definition_id,
        prefill_last_values: params.prefill_last_values,
        auto_submit: params.auto_submit,
        play_success_sound: params.play_success_sound,
        sample_scenario: params.sample_scenario,
        created_at: String(now),
        updated_at: String(now),
      };

      this.logger.debug('Created interaction type:', created);

      return created;
    } catch (error) {
      this.logger.error('Failed to create interaction type:', {
        params,
        error,
      });
      throw error;
    }
  }

  /**
   * Update an existing interaction type
   */
  async update(id: string, params: UpdateFormParams): Promise<void> {
    await this.db.init();

    try {
      // Read the stored data JSON so keys this method doesn't manage (e.g.
      // sample_scenario) survive the update.
      const rows = await this.db.exec('SELECT data FROM interaction_types WHERE id = ?', [id]);
      if (rows.length === 0) {
        throw new Error(`Interaction type not found: ${id}`);
      }
      const existingData = JSON.parse((rows[0].data as string) || '{}');

      const updatedData: Record<string, unknown> = { ...existingData };
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined) {
          updatedData[key] = value;
        }
      }

      const now = Date.now();

      await this.db.run(
        'UPDATE interaction_types SET data = ?, updated_by = ?, updated_at = ? WHERE id = ?',
        [JSON.stringify(updatedData), '', now, id], // TODO: Add updated_by when user context is available
      );

      this.logger.debug('Updated interaction type:', { id, params });
    } catch (error) {
      this.logger.error('Failed to update interaction type:', {
        id,
        params,
        error,
      });
      throw error;
    }
  }

  /**
   * Delete an interaction type
   */
  async delete(id: string): Promise<void> {
    await this.db.init();

    try {
      // Check if interaction type exists
      const form = await this.getById(id);
      if (!form) {
        throw new Error(`Interaction type not found: ${id}`);
      }

      await this.db.run('DELETE FROM interaction_types WHERE id = ?', [id]);

      this.logger.debug('Deleted interaction type:', id);
    } catch (error) {
      this.logger.error('Failed to delete interaction type:', {
        id,
        error,
      });
      throw error;
    }
  }

  /**
   * Get forms assigned to a specific asset
   */
  async getByCheckPointId(checkPointId: string): Promise<Form[]> {
    await this.db.init();

    try {
      const results = await this.db.exec(
        `SELECT it.id, it.data, it.created_by, it.created_at, it.updated_by, it.updated_at
         FROM interaction_types it
         INNER JOIN asset_interaction_types ait ON it.id = ait.interaction_type_id
         WHERE ait.asset_id = ?
         GROUP BY it.id
         ORDER BY json_extract(it.data, '$.name')`,
        [checkPointId],
      );

      return results.map((row) => this.rowToForm(row));
    } catch (error) {
      this.logger.error('Failed to get forms for asset:', {
        checkPointId,
        error,
      });
      throw error;
    }
  }

  /**
   * Assign an interaction type to an asset
   */
  async assignToCheckPoint(checkPointId: string, formId: string): Promise<void> {
    await this.db.init();

    try {
      const id = this.db.generateId();
      const now = Date.now();

      await this.db.run(
        `INSERT OR IGNORE INTO asset_interaction_types (id, asset_id, interaction_type_id, created_by, created_at, updated_by, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?)`,
        [id, checkPointId, formId, '', now, '', now],
      );

      this.logger.debug('Assigned interaction type to asset:', {
        checkPointId,
        formId,
      });
    } catch (error) {
      this.logger.error('Failed to assign interaction type to asset:', {
        checkPointId,
        formId,
        error,
      });
      throw error;
    }
  }

  /**
   * Unassign an interaction type from an asset
   */
  async unassignFromCheckPoint(checkPointId: string, formId: string): Promise<void> {
    await this.db.init();

    try {
      await this.db.run(
        `DELETE FROM asset_interaction_types
         WHERE asset_id = ? AND interaction_type_id = ?`,
        [checkPointId, formId],
      );

      this.logger.debug('Unassigned interaction type from asset:', {
        checkPointId,
        formId,
      });
    } catch (error) {
      this.logger.error('Failed to unassign interaction type from asset:', {
        checkPointId,
        formId,
        error,
      });
      throw error;
    }
  }
}
